import { z } from 'zod';
import { updateChannelSchema } from './device.js';
/**
 * Реестр обновлений Android TV (ANDROID_TV_UPDATES.md).
 *
 * Сервер отдаёт только versioned manifest: путей в storage, credentials и
 * ссылок вида `latest.apk` в ответе нет. Проверку подписи APK выполняет
 * клиент — SHA-256 защищает целостность загрузки, но не происхождение пакета.
 */
export const ANDROID_ABIS = ['arm64-v8a', 'armeabi-v7a', 'x86', 'x86_64'];
export const androidAbiSchema = z.enum(ANDROID_ABIS);
export const androidReleaseSchema = z.object({
    appId: z
        .string()
        .min(3)
        .max(128)
        .regex(/^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/, 'appId должен быть package name'),
    platform: z.literal('android-tv'),
    channel: updateChannelSchema,
    versionCode: z.number().int().positive(),
    versionName: z.string().min(1).max(40),
    minSdk: z.number().int().min(21).max(40),
    supportedAbis: z.array(androidAbiSchema).min(1),
    sizeBytes: z.number().int().nonnegative(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    /** Отпечаток release-сертификата, которым подписан APK. */
    signatureFingerprint: z
        .string()
        .regex(/^[A-F0-9]{2}(:[A-F0-9]{2})+$/)
        .nullable()
        .default(null),
    releaseNotes: z.record(z.string(), z.string().max(2000)).default({}),
    mandatory: z.boolean().default(false),
    publishedAt: z.string(),
    publishedBy: z.string().nullable().default(null),
    /** Загружен ли APK в artifact storage. Manifest без артефакта не отдаётся. */
    artifactAvailable: z.boolean().default(false),
});
/** Manifest, который получает клиент: с нашими относительными путями. */
export const androidReleaseManifestSchema = androidReleaseSchema.extend({
    manifestUrl: z.string().startsWith('/api/'),
    apkUrl: z.string().startsWith('/api/'),
});
export const updateCheckQuerySchema = z.object({
    channel: updateChannelSchema.default('stable'),
    versionCode: z.coerce.number().int().min(0).default(0),
    abi: androidAbiSchema.optional(),
    sdk: z.coerce.number().int().min(21).max(40).optional(),
});
export const updateCheckResponseSchema = z.object({
    updateAvailable: z.boolean(),
    mandatory: z.boolean(),
    channel: updateChannelSchema,
    versionCode: z.number().int().nullable(),
    versionName: z.string().nullable(),
    manifestUrl: z.string().nullable(),
    downloadUrl: z.string().nullable(),
    sha256: z.string().nullable(),
    sizeBytes: z.number().int().nonnegative().nullable(),
    /** Понятная причина, когда обновления нет: ABI, minSdk, downgrade, канал. */
    reason: z.string(),
});
export const updateReportRequestSchema = z.object({
    versionCode: z.number().int().positive(),
    result: z.enum(['downloaded', 'installed', 'failed', 'rolled-back', 'checksum-mismatch']),
    errorCode: z.string().max(64).optional(),
    message: z.string().max(500).optional(),
});
export const updateReportSchema = z.object({
    id: z.string(),
    at: z.string(),
    deviceId: z.string(),
    channel: updateChannelSchema,
    versionCode: z.number().int(),
    result: z.string(),
    errorCode: z.string().nullable(),
    message: z.string().nullable(),
});
/** Запрос на публикацию релиза из Admin Panel. APK кладётся в artifact storage. */
export const publishReleaseRequestSchema = androidReleaseSchema
    .omit({ platform: true, publishedAt: true, publishedBy: true, artifactAvailable: true })
    .extend({
    /** Разрешить публикацию versionCode ниже текущего (по умолчанию запрещено). */
    allowDowngrade: z.boolean().default(false),
});
export const releaseListResponseSchema = z.object({
    releases: z.array(androidReleaseSchema),
    current: z.record(z.string(), androidReleaseSchema.nullable()),
    reports: z.array(updateReportSchema),
});
//# sourceMappingURL=updates.js.map